import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Action, Actions, ofActionSuccessful, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { filter, mergeMap, tap } from 'rxjs/operators';
import { DirectoriesService, OrganisationUnitEntryRemoteDto } from '../../../../../generated/swagger';
import { DirectoryColumn } from '../../../../samwebDescription/samweb/directory/directory-column';
import { LanguageType } from '../../../../samwebDescription/samweb/settings/language-types';
import { SessionUpdated } from '../../../session/session.actions';
import { SettingsState } from '../../../settings/settings.state';
import { DirectoryStructureLoaded, LoadDirectoryStructure } from './directory-structure.actions';

export interface DirectoryStructureStateModel {
   organisationUnits: OrganisationUnitEntryRemoteDto[];
   directoryStructureId: string;
}

@State<DirectoryStructureStateModel>({
   name: 'directoryStructure',
   defaults: {
      organisationUnits: [],
      directoryStructureId: null,
   },
})
@Injectable()
export class DirectoryStructureState {
   constructor(private directoriesService: DirectoriesService, private actions$: Actions, private store: Store) {
      actions$
         .pipe(
            ofActionSuccessful(SessionUpdated),
            filter((x: SessionUpdated) => x.sessionInfo.isAuthenticated),
            tap(() => store.dispatch(new LoadDirectoryStructure()))
         )
         .subscribe();
   }

   @Selector([DirectoryStructureState])
   public static organisationUnits(state: DirectoryStructureStateModel) {
      return state.organisationUnits;
   }

   @Selector([DirectoryStructureState])
   public static directoryStructureId(state: DirectoryStructureStateModel) {
      return state.directoryStructureId;
   }

   @Selector([DirectoryStructureState.organisationUnits])
   public static availableDirectoryColumns(organisationUnits: OrganisationUnitEntryRemoteDto[]): DirectoryColumn[] {
      // flat map the whole organisation structure to get all possible columns for directory browsing!
      const result = _(organisationUnits)
         .flatMap((organisationUnit) => {
            return _(organisationUnit.organisationColumns)
               .map(
                  (organisationColumn): DirectoryColumn =>
                     new DirectoryColumn(organisationUnit.organizationalUnitName, organisationUnit.organizationalUnitNumber, organisationColumn.columnKey)
               )
               .filter((directoryColumn) => directoryColumn.columnTyp.canBeDisplayed)
               .value();
         })
         .value();
      return result;
   }

   @Selector([DirectoryStructureState.availableDirectoryColumns])
   public static availableDirectoryColumnsOrdered(availableDirectoryColumns: DirectoryColumn[]): DirectoryColumn[] {
      return _.orderBy(availableDirectoryColumns, (column) => column.organisationUnitNumber.toLowerCase());
   }

   @Selector()
   public static filterByOrgUnitNumber() {
      return (available: DirectoryColumn[], orgUnitNumber: string) => {
         return available.filter((col) => col.organisationUnitNumber === orgUnitNumber);
      };
   }

   @Selector([DirectoryStructureState.availableDirectoryColumns, DirectoryStructureState.filterByOrgUnitNumber])
   public static availablePhoneticDirectoryColumns(
      available: DirectoryColumn[],
      filterByOrgUnitNumber: (available: DirectoryColumn[], orgUnitNumber: string) => DirectoryColumn[]
   ) {
      return filterByOrgUnitNumber(available, '00');
   }

   @Selector([DirectoryStructureState.organisationUnits])
   public static organisationDirectoryColumns(organisationUnits: OrganisationUnitEntryRemoteDto[]): DirectoryColumn[] {
      // Filter for org structure, only orgunits part of subscriber should be displayed.
      const allowedOrgStructureFields = organisationUnits
         .filter((org) => org.isChildStructureOf === '00')
         .map((org) => 'fld_org' + org.organizationalUnitNumber);
      // flat map the whole organisation structure to get all possible columns for directory browsing!
      const result = _(organisationUnits)
         .flatMap((organisationUnit) => {
            return _(organisationUnit.organisationColumns)
               .map(
                  (organisationColumn): DirectoryColumn =>
                     new DirectoryColumn(organisationUnit.organizationalUnitName, organisationUnit.organizationalUnitNumber, organisationColumn.columnKey)
               )
               .filter((directoryColumn) => directoryColumn.columnTyp.isOrganisation)
               .filter((directoryColumn) => allowedOrgStructureFields.includes(directoryColumn.columnTyp.value))
               .value();
         })
         .value();
      return result;
   }

   @Selector([DirectoryStructureState.organisationUnits])
   public static allDirectoryColumns(organisationUnits: OrganisationUnitEntryRemoteDto[]): DirectoryColumn[] {
      // flat map the whole organisation structure to get all possible columns for directory browsing!
      const result = _(organisationUnits)
         .flatMap((organisationUnit) => {
            return _(organisationUnit.organisationColumns)
               .map(
                  (organisationColumn): DirectoryColumn =>
                     new DirectoryColumn(organisationUnit.organizationalUnitName, organisationUnit.organizationalUnitNumber, organisationColumn.columnKey)
               )
               .value();
         })
         .value();
      return result;
   }

   @Selector([DirectoryStructureState.organisationUnits])
   public static selectedDirectoryOrganisationEntry(organisationUnits: OrganisationUnitEntryRemoteDto[]): OrganisationUnitEntryRemoteDto {
      return _(organisationUnits).find((entry) => entry.isDefaultOrganisationDirectory);
   }

   @Selector([DirectoryStructureState.allDirectoryColumns, DirectoryStructureState.selectedDirectoryOrganisationEntry])
   public static selectedDirectoryDirectoryColumn(columns: DirectoryColumn[], orgEntry: OrganisationUnitEntryRemoteDto): DirectoryColumn {
      return columns.find((col) => col.organisationUnitColumnKey === 'fld_org' + orgEntry.organizationalUnitNumber);
   }

   @Selector([DirectoryStructureState.availableDirectoryColumns, DirectoryStructureState.organisationDirectoryColumns])
   public static editableColumns(availableColumns: DirectoryColumn[], organisationColumns: DirectoryColumn[]): DirectoryColumn[] {
      return availableColumns.concat(organisationColumns);
   }

   @Selector([DirectoryStructureState.organisationUnits])
   public static getDirectoryColumnFromKey(organisationUnits: OrganisationUnitEntryRemoteDto[]): (columnKey: string) => DirectoryColumn {
      // flat map the whole organisation structure to get all possible columns for directory browsing!
      return (columnKey: string) => {
         return _(organisationUnits)
            .flatMap((organisationUnit) => {
               return _(organisationUnit.organisationColumns)
                  .map(
                     (organisationColumn): DirectoryColumn =>
                        new DirectoryColumn(organisationUnit.organizationalUnitName, organisationUnit.organizationalUnitNumber, organisationColumn.columnKey)
                  )
                  .filter((columns) => columns.organisationUnitColumnKey === columnKey)
                  .value();
            })
            .head();
      };
   }

   @Selector([DirectoryStructureState.organisationUnits, SettingsState.getCurrentLanguage, SettingsState.getFallbackLanguage])
   public static columnTitleForDirectoryColumn(
      organisationUnits: OrganisationUnitEntryRemoteDto[],
      currentLanguage: LanguageType,
      fallbackLanguage: LanguageType
   ): (directoryColumnType: DirectoryColumn, translationService: TranslateService) => string {
      return (directoryColumnType: DirectoryColumn, translationService: TranslateService) => {
         if (_.isNil(directoryColumnType)) {
            return '';
         }

         if (!_.isNil(directoryColumnType.columnTyp?.translationKey)) {
            return translationService.instant(directoryColumnType.columnTyp.translationKey);
         }

         const organisationUnit = _(organisationUnits)
            .filter((x) => x.organizationalUnitNumber === directoryColumnType.organisationUnitNumber)
            .first();

         if (_.isNil(organisationUnit)) {
            return '';
         }

         const columnTranslationValues = _(organisationUnit.organisationColumns ?? [])
            .filter((x) => x.columnKey === directoryColumnType.organisationUnitColumnKey)
            .first();

         if (_.isNil(columnTranslationValues)) {
            return '';
         }

         let columnTranslation = columnTranslationValues.translationValues[currentLanguage.samwinLegacyLanguageCode];
         if (_.isNil(columnTranslation)) {
            columnTranslation = columnTranslationValues.translationValues[fallbackLanguage.samwinLegacyLanguageCode];
         }
         // still null?
         if (_.isNil(columnTranslation)) {
            return `${organisationUnit.organizationalUnitName} - ${directoryColumnType.directoryColumnKey}`;
         }
         if (organisationUnit.organizationalUnitNumber === '00' || columnTranslation.isCustomTranslation) {
            return columnTranslation.translatedValue;
         }
         return `${organisationUnit.organizationalUnitName} - ${columnTranslation.translatedValue}`;
      };
   }

   @Selector([DirectoryStructureState.organisationUnits])
   public static getStructureNameForStructureNumber(organisationUnits: OrganisationUnitEntryRemoteDto[]) {
      return (organisationUnitNumber: string) => {
         return _(organisationUnits)
            .filter((x) => x.organizationalUnitNumber === organisationUnitNumber)
            .head()?.organizationalUnitName;
      };
   }

   @Selector([DirectoryStructureState.organisationUnits])
   public static getParentStructureForStructureNumber(organisationUnits: OrganisationUnitEntryRemoteDto[]) {
      return (organisationUnitNumber: string) => {
         return _(organisationUnits)
            .filter((x) => x.organizationalUnitNumber === organisationUnitNumber)
            .head()?.isChildStructureOf;
      };
   }

   @Action(LoadDirectoryStructure)
   public loadDirectoryStructure(ctx: StateContext<DirectoryStructureStateModel>, action: LoadDirectoryStructure) {
      return this.directoriesService
         .directoryStructure()
         .pipe(mergeMap((x) => ctx.dispatch(new DirectoryStructureLoaded(x.organizationalUnit, x.directoryStructureId))));
   }

   @Action(DirectoryStructureLoaded)
   public directoryStructureLoaded(ctx: StateContext<DirectoryStructureStateModel>, action: DirectoryStructureLoaded) {
      ctx.patchState({
         organisationUnits: action.resultEntries,
         directoryStructureId: action.directoryStructureId,
      });
   }
}
